import React, { useState, useEffect } from 'react';
import { PasswordGeneratorService, PasswordOptions } from '../../lib/services/passwordGeneratorService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types';
import './PasswordGenerator.css';

export const PasswordGenerator: React.FC = () => {
  const [output, setOutput] = useState('');
  const [length, setLength] = useState(16);
  const [quantity, setQuantity] = useState(1);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [strength, setStrength] = useState<string>('');

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);

    const options: PasswordOptions = {
      length,
      includeUppercase,
      includeLowercase,
      includeNumbers,
      includeSymbols,
      quantity
    };

    try {
      const result = await PasswordGeneratorService.generate(options);
      setOutput(result.outputData as string);
      setStrength(result.metadata?.strength as string || '');
      setError(null);
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.PASSWORD_GENERATOR);
      setError(errorMessage);
      setOutput('');
      setStrength('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGenerate();
  }, [length, quantity, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Password';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="password-generator">
      <div style={{ marginBottom: '2rem' }}>
        <h1>Password Generator</h1>
        <p>
          Generate secure random passwords with customizable length and character sets.
          Use strong passwords to protect your accounts.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Length: {length}
          </label>
          <input
            type="range"
            min="8"
            max="128"
            value={length}
            onChange={(e) => setLength(parseInt(e.target.value, 10))}
            data-testid="length-slider"
          />
        </div>

        <div className="form-group">
          <label>
            Quantity:
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
            data-testid="quantity-input"
          />
        </div>

        <div className="form-group">
          <label>Character Sets:</label>
          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                checked={includeUppercase}
                onChange={(e) => setIncludeUppercase(e.target.checked)}
                data-testid="uppercase-checkbox"
              />
              Uppercase (A-Z)
            </label>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                checked={includeLowercase}
                onChange={(e) => setIncludeLowercase(e.target.checked)}
                data-testid="lowercase-checkbox"
              />
              Lowercase (a-z)
            </label>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                checked={includeNumbers}
                onChange={(e) => setIncludeNumbers(e.target.checked)}
                data-testid="numbers-checkbox"
              />
              Numbers (0-9)
            </label>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                checked={includeSymbols}
                onChange={(e) => setIncludeSymbols(e.target.checked)}
                data-testid="symbols-checkbox"
              />
              Symbols (!@#$%^&*)
            </label>
          </div>
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        {strength && (
          <div className="status-message" style={{ 
            backgroundColor: strength.includes('Very Strong') ? 'rgba(40, 167, 69, 0.1)' : 
                           strength.includes('Strong') ? 'rgba(255, 193, 7, 0.1)' : 
                           'rgba(220, 53, 69, 0.1)',
            color: strength.includes('Very Strong') ? '#28a745' : 
                   strength.includes('Strong') ? '#ffc107' : 
                   '#dc3545',
            border: strength.includes('Very Strong') ? '1px solid rgba(40, 167, 69, 0.2)' : 
                    strength.includes('Strong') ? '1px solid rgba(255, 193, 7, 0.2)' : 
                    '1px solid rgba(220, 53, 69, 0.2)'
          }}>
            Password Strength: {strength}
          </div>
        )}

        <div className="form-group">
          <label>
            Generated Password{quantity > 1 ? 's' : ''}:
          </label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Generating...' : 'Password will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
          />
          {isLoading && (
            <div className="loading-indicator">Generating...</div>
          )}
        </div>

        <div className="button-container">
          <button
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
